import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
// @ts-ignore - Unused imports FiEye, FiClock
import {
    FiPlus,
    FiSearch,
    FiEdit3,
    FiTrash2,
    // @ts-ignore - Unused import
    FiEye,
    FiUsers,
    // @ts-ignore - Unused import
    FiClock,
    FiAlertCircle,
    FiRefreshCw
} from 'react-icons/fi'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
// @ts-ignore - Unused import deleteGig
import { fetchUserGigs, setUserGigsCurrentPage, deleteGig } from '@/store/slices/gigsSlice'
// @ts-ignore - Unused import Gig
import { Gig } from '@/types/gig'
import GigCard from '@/components/gigs/GigCard'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { gigApi } from '@/services/api/gigs'

type GigStatus = 'all' | 'active' | 'filled' | 'completed' | 'cancelled' | 'expired' | 'pending' | 'rejected'

const MyGigsPage: React.FC = () => {
    const { user } = useAppSelector(state => state.auth)
    const {
        userGigs,
        userGigsLoading,
        userGigsError,
        userGigsTotalCount,
        userGigsCurrentPage,
        userGigsTotalPages
    } = useAppSelector(state => state.gigs)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [activeTab, setActiveTab] = useState<GigStatus>('all')
    const [searchTerm, setSearchTerm] = useState('')
    const [refreshing, setRefreshing] = useState(false)

    const gigsPerPage = 12

    // Check if user is not an employer
    useEffect(() => {
        if (user && user.role !== 'employer') {
            navigate('/dashboard')
        }
    }, [user, navigate])

    // Fetch gigs
    const fetchGigs = (status: GigStatus = 'all', page: number = 1) => {
        const params: any = {
            page,
            limit: gigsPerPage,
        }

        if (status === 'pending' || status === 'rejected') {
            params.moderationStatus = status
        } else if (status !== 'all') {
            params.status = status
        }

        // Note: For now we're not implementing search functionality in Redux
        // This can be added later if needed

        dispatch(fetchUserGigs(params))
    }

    useEffect(() => {
        fetchGigs(activeTab, userGigsCurrentPage)
    }, [activeTab, userGigsCurrentPage, dispatch])

    const handleTabChange = (tab: GigStatus) => {
        setActiveTab(tab)
        dispatch(setUserGigsCurrentPage(1))
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
        dispatch(setUserGigsCurrentPage(1))
    }

    const handleDeleteGig = async (gigId: string) => {
        try {
            await gigApi.deleteGig(gigId)
            // Refresh the gigs list
            fetchGigs(activeTab, userGigsCurrentPage)
        } catch (error) {
            console.error('Failed to delete gig:', error)
            alert('Failed to delete gig. Please try again.')
        }
    }

    const handleReactivateGig = async (gigId: string) => {
        const newEndDate = prompt('Enter new end date (optional, leave empty for no end date):')

        try {
            await gigApi.reactivateGig(gigId, newEndDate || undefined)
            // Refresh the gigs list
            fetchGigs(activeTab, userGigsCurrentPage)
            alert('Gig reactivated successfully!')
        } catch (error) {
            console.error('Failed to reactivate gig:', error)
            alert('Failed to reactivate gig. Please try again.')
        }
    }

    const handleRefresh = async () => {
        setRefreshing(true)
        try {
            fetchGigs(activeTab, userGigsCurrentPage)
        } catch (error) {
            console.error('Failed to refresh gigs:', error)
        } finally {
            setRefreshing(false)
        }
    }

    const getTabCount = (status: GigStatus) => {
        if (status === 'all') return userGigsTotalCount
        if (status === 'expired') return (userGigs || []).filter(gig => gig.isExpired).length
        if (status === 'pending') return (userGigs || []).filter(gig => gig.moderationStatus === 'pending').length
        if (status === 'rejected') return (userGigs || []).filter(gig => gig.moderationStatus === 'rejected').length
        return (userGigs || []).filter(gig => gig.status === status).length
    }

    const tabs = [
        { id: 'all' as GigStatus, label: 'All Gigs', count: getTabCount('all') },
        { id: 'pending' as GigStatus, label: 'Pending Approval', count: getTabCount('pending') },
        { id: 'active' as GigStatus, label: 'Active', count: getTabCount('active') },
        { id: 'expired' as GigStatus, label: 'Expired', count: getTabCount('expired') },
        { id: 'filled' as GigStatus, label: 'Filled', count: getTabCount('filled') },
        { id: 'completed' as GigStatus, label: 'Completed', count: getTabCount('completed') },
        { id: 'cancelled' as GigStatus, label: 'Cancelled', count: getTabCount('cancelled') },
        { id: 'rejected' as GigStatus, label: 'Rejected', count: getTabCount('rejected') },
    ]

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            case 'expired':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
            case 'filled':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
            case 'completed':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
            case 'cancelled':
                return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
        }
    }

    const getModerationStatusColor = (moderationStatus: string) => {
        switch (moderationStatus) {
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
        }
    }

    const getModerationStatusIcon = (moderationStatus: string) => {
        switch (moderationStatus) {
            case 'approved':
                return '✅'
            case 'pending':
                return '⏳'
            case 'rejected':
                return '❌'
            default:
                return '?'
        }
    }

    return (
        <>
            <Helmet>
                <title>My Gigs - SideGO</title>
                <meta name="description" content="Manage your posted gigs and track applications." />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                            My Gigs
                        </h1>
                        <p className="text-xs sm:text-sm md:text-lg text-gray-600 dark:text-gray-400 mt-2">
                            Manage your posted gigs and track applications
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="inline-flex items-center gap-2 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                        >
                            <FiRefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <Link
                            to="/dashboard/gigs/create"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors text-sm"
                        >
                            <FiPlus className="w-4 h-4" />
                            New Gig
                        </Link>
                    </div>
                </div>

                {/* Search */}
                <div className="mb-6">
                    <div className="relative max-w-md">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search your gigs..."
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs sm:text-sm md:text-base"
                        />
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-8">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-hide">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm md:text-base transition-colors ${activeTab === tab.id
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    {tab.label}
                                    {tab.count > 0 && (
                                        <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs sm:text-xs md:text-sm">
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Status Information */}
                {activeTab === 'pending' && getTabCount('pending') > 0 && (
                    <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                            <span className="text-lg">⏳</span>
                            <p className="text-xs sm:text-sm md:text-base font-medium">
                                Your gigs are pending admin approval. They will be publicly visible once approved by our team.
                            </p>
                        </div>
                    </div>
                )}

                {activeTab === 'rejected' && getTabCount('rejected') > 0 && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                            <span className="text-lg">❌</span>
                            <p className="text-xs sm:text-sm md:text-base font-medium">
                                These gigs were rejected by our moderation team. Please review our gig posting guidelines and repost with necessary changes.
                            </p>
                        </div>
                    </div>
                )}

                {/* Content */}
                {userGigsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <LoadingSpinner />
                    </div>
                ) : userGigsError ? (
                    <div className="text-center py-12">
                        <FiAlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xs sm:text-sm md:text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Error Loading Gigs
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6">
                            {userGigsError}
                        </p>
                        <button
                            onClick={() => fetchGigs(activeTab, userGigsCurrentPage)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                ) : (userGigs || []).length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <span className="text-gray-400 font-bold text-4xl">$</span>
                        </div>
                        <h3 className="text-xs sm:text-sm md:text-lg font-medium text-gray-900 dark:text-white mb-2">
                            {searchTerm ? 'No gigs found' : 'No gigs posted yet'}
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 mb-6">
                            {searchTerm
                                ? 'Try adjusting your search terms or filters.'
                                : 'Start by posting your first gig to attract talented workers.'
                            }
                        </p>
                        {!searchTerm && (
                            <Link
                                to="/dashboard/gigs/create"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                            >
                                <FiPlus className="w-5 h-5" />
                                Post Your First Gig
                            </Link>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Gigs Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            {(userGigs || []).filter((gig) => {
                                if (activeTab === 'all') return true
                                if (activeTab === 'expired') return gig.isExpired
                                if (activeTab === 'pending') return gig.moderationStatus === 'pending'
                                if (activeTab === 'rejected') return gig.moderationStatus === 'rejected'
                                return gig.status === activeTab
                            }).map((gig) => (
                                <div key={gig._id} className="relative">
                                    <GigCard
                                        gig={gig}
                                        showEmployer={false}
                                        currentUser={user}
                                    />

                                    {/* Action buttons overlay */}
                                    <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Link
                                            to={`/gigs/${gig._id}/applications`}
                                            className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                            title="View Applications"
                                        >
                                            <FiUsers className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                        </Link>
                                        {gig.isExpired ? (
                                            <button
                                                onClick={() => handleReactivateGig(gig._id)}
                                                className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                                title="Reactivate Gig"
                                            >
                                                <FiRefreshCw className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            </button>
                                        ) : (
                                            <Link
                                                to={`/dashboard/gigs/${gig._id}/edit`}
                                                className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                title="Edit Gig"
                                            >
                                                <FiEdit3 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                            </Link>
                                        )}
                                        <button
                                            onClick={() => handleDeleteGig(gig._id)}
                                            className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            title="Delete Gig"
                                        >
                                            <FiTrash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                                        </button>
                                    </div>

                                    {/* Status badge */}
                                    <div className="absolute top-4 left-4 flex flex-col gap-1">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(gig.status)}`}>
                                            {gig.status.charAt(0).toUpperCase() + gig.status.slice(1)}
                                        </span>
                                        {gig.moderationStatus && (
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getModerationStatusColor(gig.moderationStatus)}`}>
                                                <span className="mr-1">{getModerationStatusIcon(gig.moderationStatus)}</span>
                                                {gig.moderationStatus === 'pending' ? 'Pending Review' :
                                                    gig.moderationStatus === 'approved' ? 'Approved' : 'Rejected'}
                                            </span>
                                        )}
                                        {gig.isExpired && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                                                Expired
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {userGigsTotalPages > 1 && (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                                    <span>
                                        Showing {((userGigsCurrentPage - 1) * gigsPerPage) + 1} to {Math.min(userGigsCurrentPage * gigsPerPage, userGigsTotalCount)} of {userGigsTotalCount} gigs
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => dispatch(setUserGigsCurrentPage(userGigsCurrentPage - 1))}
                                        disabled={userGigsCurrentPage === 1}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm md:text-base"
                                    >
                                        Previous
                                    </button>

                                    {Array.from({ length: Math.min(5, userGigsTotalPages) }, (_, i) => {
                                        const page = userGigsCurrentPage <= 3
                                            ? i + 1
                                            : userGigsCurrentPage >= userGigsTotalPages - 2
                                                ? userGigsTotalPages - 4 + i
                                                : userGigsCurrentPage - 2 + i

                                        if (page < 1 || page > userGigsTotalPages) return null

                                        return (
                                            <button
                                                key={page}
                                                onClick={() => dispatch(setUserGigsCurrentPage(page))}
                                                className={`px-3 py-2 border rounded-lg transition-colors text-xs sm:text-sm md:text-base ${userGigsCurrentPage === page
                                                    ? 'bg-primary-600 border-primary-600 text-white'
                                                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        )
                                    })}

                                    <button
                                        onClick={() => dispatch(setUserGigsCurrentPage(userGigsCurrentPage + 1))}
                                        disabled={userGigsCurrentPage === userGigsTotalPages}
                                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm md:text-base"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    )
}

export default MyGigsPage